import { useState } from 'react';
import { AxiosError } from 'axios';
import {
  IErrorOMSService,
  IErrorReturnService,
  IGenericError,
  ILegacyNestedError,
} from '../models/returns/returns-common';
import { getAPIErrorData } from '../utils/returns/returns-helper';

interface FetchFnResponse<T> {
  data: Awaited<T> | null;
  error: IErrorReturnService | IErrorOMSService | Error | null;
}

export interface FetchState<T> {
  data: T | null;
  loading: boolean;
  error: IErrorReturnService | IErrorOMSService | Error | null;
  errorMsg: string | null;
  isFetched: boolean;
  fetchData: (fetchFunction: () => Promise<T>) => Promise<FetchFnResponse<T>>;
}

const useFetchState = <T>(): FetchState<T> => {
  const [data, setData] = useState<T | null>(null);
  const [isFetched, setIsFetched] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<
    IErrorReturnService | IErrorOMSService | Error | null
  >(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchData = async (fetchFunction: () => Promise<T>) => {
    let result: Awaited<T> | null = null;
    setLoading(true);
    try {
      result = await fetchFunction();
      setData(result);
      setError(null);
      setErrorMsg(null);
      return { data: result, error: null };
    } catch (err) {
      setData(null);
      // Check (loosely) if it's an AxiosError
      if ('response' in (err as AxiosError)) {
        const errorData = getAPIErrorData(
          err as AxiosError<IGenericError | ILegacyNestedError>,
        );
        if (!errorData) {
          const tempError = new Error('No error data found');
          setError(tempError);
          setErrorMsg('No error data found');
          return { data: null, error: tempError };
        }
        setError(errorData);
        setErrorMsg(errorData?.description);
        return { data: null, error: errorData };
      }
      if (err instanceof Error) {
        setError(err);
        setErrorMsg(err.message);
        return { data: null, error: err };
      }
      return { data: null, error: err as Error };
    } finally {
      setLoading(false);
      setIsFetched(true);
    }
  };

  return {
    data,
    loading,
    error,
    errorMsg,
    isFetched,
    fetchData,
  };
};

export default useFetchState;
